import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchReviews } from '../../services/reviewService';
import { Review } from '../../types';

const formatReviewDate = (review: Review): string => {
  if (review.dateRelative) return review.dateRelative;
  if (review.dateIso) {
    try {
      return new Date(review.dateIso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return review.dateIso;
    }
  }
  return '';
};

const ReviewSection: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const items = await fetchReviews({ rating: 5 });
        setReviews(items.filter((r) => r.text?.trim()));
      } catch (e) {
        console.error('Failed to load reviews:', e);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || reviews.length === 0) {
    return null;
  }

  const duplicated = [...reviews, ...reviews];

  return (
    <section className="py-2xl md:py-3xl bg-neutral-light-gray">
      <div className="container">
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-secondary text-neutral-charcoal mb-3">
            What Travelers Say
          </h2>
          <p className="body-regular text-secondary-slate max-w-2xl mx-auto">
            Five-star stories from our Google reviews
          </p>
        </motion.div>

        <motion.div
          className="overflow-hidden rounded-2xl border border-neutral-medium-gray bg-neutral-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="review-marquee">
            <div className="review-track">
              {duplicated.map((review, idx) => (
                <div className="review-card" key={`${review.reviewId || review.id}-${idx}`}>
                  <div className="flex items-center gap-1 mb-3 text-primary-gold" aria-label="5 star rating">
                    {'★★★★★'}
                  </div>
                  <p className="body-regular text-neutral-dark-gray line-clamp-4 mb-4 flex-1">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="mt-auto pt-3 border-t border-neutral-medium-gray">
                    <p className="font-semibold text-neutral-charcoal font-[var(--font-secondary)]">
                      {review.author}
                    </p>
                    {formatReviewDate(review) && (
                      <p className="body-small text-secondary-slate mt-1">
                        {formatReviewDate(review)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="text-center mt-8">
          <Link
            to="/reviews"
            className="btn btn-secondary inline-flex"
          >
            View All Reviews
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ReviewSection;
